import classNames from 'classnames'
import { ReactNode } from 'react'

import { t } from '../../../common/i18n.js'
import { Language } from '../../../common/types/i18n.js'
import { PlaytimeLeaderboardUser } from '../../../common/types/playtime.js'
import { Link } from '../link/link.js'
import { UserBubble } from '../user-bubble/user-bubble.js'
import './leaderboard-table.scss'

export interface LeaderboardTableProps {
    users: PlaytimeLeaderboardUser[]
    currentUserId?: number
    scoreKey?: 'totalScore' | 'scoreWithoutAnniversary'
    lang: Language
    /** When set, user names render as links with hover-preview callbacks. */
    userLink?: {
        toHref: (userId: number) => string
        onHoverStart: (userId: number) => void
        onHoverEnd: () => void
        /** Card anchored to the hovered user's name; null renders nothing. */
        hoverCard: { userId: number; content: ReactNode; placement: 'above' | 'below' } | null
    }
}

const CrownIcon = ({ place }: { place: 1 | 2 | 3 }) => (
    <svg
        width="22"
        height="20"
        viewBox="0 0 22 20"
        fill="none"
        className={`leaderboard-table__crown leaderboard-table__crown--${place}`}
    >
        <path
            d="M1.79989 4.20008L5.99989 7.00008L10.1859 1.14008C10.2784 1.01045 10.4005 0.904788 10.5421 0.831885C10.6837 0.758981 10.8406 0.720947 10.9999 0.720947C11.1591 0.720947 11.3161 0.758981 11.4577 0.831885C11.5993 0.904788 11.7214 1.01045 11.8139 1.14008L15.9999 7.00008L20.1999 4.20008C20.3588 4.09433 20.5447 4.03629 20.7356 4.03282C20.9265 4.02935 21.1144 4.0806 21.2771 4.1805C21.4398 4.28041 21.5705 4.4248 21.6537 4.59662C21.737 4.76843 21.7693 4.96048 21.7469 5.15008L20.1039 19.1171C20.0752 19.3603 19.9583 19.5846 19.7753 19.7474C19.5922 19.9101 19.3558 20 19.1109 20.0001H2.88889C2.64395 20 2.40755 19.9101 2.22451 19.7474C2.04148 19.5846 1.92454 19.3603 1.89589 19.1171L0.252885 5.14908C0.230685 4.95957 0.263171 4.76765 0.346506 4.596C0.429842 4.42435 0.560548 4.28012 0.723196 4.18034C0.885845 4.08057 1.07364 4.02941 1.26442 4.03291C1.45521 4.0364 1.641 4.09441 1.79989 4.20008ZM10.9999 14.0001C11.5303 14.0001 12.039 13.7894 12.4141 13.4143C12.7892 13.0392 12.9999 12.5305 12.9999 12.0001C12.9999 11.4696 12.7892 10.9609 12.4141 10.5859C12.039 10.2108 11.5303 10.0001 10.9999 10.0001C10.4695 10.0001 9.96074 10.2108 9.58567 10.5859C9.2106 10.9609 8.99989 11.4696 8.99989 12.0001C8.99989 12.5305 9.2106 13.0392 9.58567 13.4143C9.96074 13.7894 10.4695 14.0001 10.9999 14.0001Z"
            fill="currentColor"
        />
    </svg>
)

export const LeaderboardTable = ({
    users,
    currentUserId,
    scoreKey = 'scoreWithoutAnniversary',
    lang,
    userLink,
}: LeaderboardTableProps) => {
    const lt = t.playtime.leaderboardTable

    return (
        <div className="leaderboard-table">
            <table className="leaderboard-table__table">
                <thead>
                    <tr>
                        <th className="leaderboard-table__th leaderboard-table__th--rank">#</th>
                        <th className="leaderboard-table__th">{lt.user(lang)}</th>
                        <th className="leaderboard-table__th leaderboard-table__th--score">
                            {lt.score(lang)}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user, index) => (
                        <tr
                            key={user.id}
                            className={classNames(
                                'leaderboard-table__row',
                                user.id === currentUserId && 'leaderboard-table__row--current',
                            )}
                        >
                            <td className="leaderboard-table__td leaderboard-table__td--rank">
                                <span className="leaderboard-table__spot">{index + 1}.</span>
                            </td>
                            <td className="leaderboard-table__td leaderboard-table__td--user">
                                <div className="leaderboard-table__user-cell">
                                    {index < 3 && (
                                        <div className="leaderboard-table__crown-wrapper">
                                            <CrownIcon place={(index + 1) as 1 | 2 | 3} />
                                        </div>
                                    )}
                                    <UserBubble
                                        name={null}
                                        avatar={user.avatar}
                                        size="small"
                                        lang={lang}
                                    />
                                    {userLink ? (
                                        <span
                                            className="leaderboard-table__name-wrapper"
                                            onMouseEnter={() => userLink.onHoverStart(user.id)}
                                            onMouseLeave={() => userLink.onHoverEnd()}
                                        >
                                            <Link
                                                className="leaderboard-table__name-link"
                                                href={userLink.toHref(user.id)}
                                            >
                                                {user.name}
                                            </Link>
                                            {userLink.hoverCard?.userId === user.id && (
                                                <span
                                                    className={classNames(
                                                        'leaderboard-table__hover-card',
                                                        userLink.hoverCard.placement === 'above' &&
                                                            'leaderboard-table__hover-card--above',
                                                    )}
                                                >
                                                    {userLink.hoverCard.content}
                                                </span>
                                            )}
                                        </span>
                                    ) : (
                                        <span className="leaderboard-table__name">{user.name}</span>
                                    )}
                                </div>
                            </td>
                            <td className="leaderboard-table__td leaderboard-table__td--score">
                                <span className="leaderboard-table__score-value">
                                    {user[scoreKey]}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}
